import React, { useState, useEffect } from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  MapPin, Plus, Trash2, Edit, Save, X, Loader2,
  DollarSign, Clock, ArrowRight, Settings
} from 'lucide-react';
import api from '../../../utils/api';
import { toast } from '../../../hooks/use-toast';

const RouteStopsManager = ({ routeId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [routeData, setRouteData] = useState(null);
  const [stops, setStops] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStop, setNewStop] = useState({
    city: '',
    station: '',
    arrival_offset: '',
    stop_duration: 15,
    distance_from_origin: '',
    fixed_price: ''
  });

  useEffect(() => {
    if (routeId) {
      fetchRouteStops();
    }
  }, [routeId]);

  const fetchRouteStops = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/bus/routes/${routeId}/stops`);
      setRouteData(response.data.route);
      setStops(response.data.stops || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load route stops', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddStop = async () => {
    if (!newStop.city || !newStop.station || !newStop.arrival_offset) {
      toast({ title: 'Error', description: 'City, station, and arrival time are required', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await api.post(`/admin/bus/routes/${routeId}/stops`, {
        city: newStop.city,
        station: newStop.station,
        arrival_offset: parseInt(newStop.arrival_offset),
        stop_duration: parseInt(newStop.stop_duration) || 15,
        distance_from_origin: newStop.distance_from_origin ? parseFloat(newStop.distance_from_origin) : null,
        fixed_price: newStop.fixed_price ? parseFloat(newStop.fixed_price) : null
      });

      toast({ title: 'Success', description: `Stop '${newStop.city}' added` });
      setNewStop({ city: '', station: '', arrival_offset: '', stop_duration: 15, distance_from_origin: '', fixed_price: '' });
      setShowAddForm(false);
      fetchRouteStops();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to add stop', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStop = async (index, updatedStop) => {
    setSaving(true);
    try {
      await api.put(`/admin/bus/routes/${routeId}/stops/${index}`, {
        city: updatedStop.city,
        station: updatedStop.station,
        arrival_offset: updatedStop.arrivalOffset,
        stop_duration: updatedStop.stopDuration,
        distance_from_origin: updatedStop.distanceFromOrigin,
        fixed_price: updatedStop.fixedPrice
      });

      toast({ title: 'Success', description: 'Stop updated' });
      setEditingIndex(null);
      fetchRouteStops();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update stop', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStop = async (index) => {
    if (!window.confirm('Are you sure you want to remove this stop?')) return;

    setSaving(true);
    try {
      await api.delete(`/admin/bus/routes/${routeId}/stops/${index}`);
      toast({ title: 'Success', description: 'Stop removed' });
      fetchRouteStops();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to remove stop', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePricingModel = async (model) => {
    setSaving(true);
    try {
      await api.put(`/admin/bus/routes/${routeId}/pricing-model?pricing_model=${model}&allow_intermediate=true`);
      toast({ title: 'Success', description: `Pricing model updated to ${model}` });
      fetchRouteStops();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update pricing model', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="route-stops-manager">
      {/* Route Info Header */}
      {routeData && (
        <Card className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{routeData.operatorName}</p>
              <h3 className="text-lg font-bold flex items-center gap-2">
                {routeData.origin?.city}
                <ArrowRight className="h-4 w-4 text-gray-400" />
                {routeData.destination?.city}
              </h3>
              <p className="text-sm text-gray-500">
                {routeData.distanceKm} km • {formatDuration(routeData.estimatedDuration)} • Base: K{routeData.basePrice}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-medium">Pricing Model</p>
              <div className="flex gap-1 mt-1">
                <Button
                  size="sm"
                  variant={routeData.pricingModel === 'distance_based' ? 'default' : 'outline'}
                  onClick={() => handleUpdatePricingModel('distance_based')}
                  disabled={saving}
                >
                  Distance
                </Button>
                <Button
                  size="sm"
                  variant={routeData.pricingModel === 'fixed_per_stop' ? 'default' : 'outline'}
                  onClick={() => handleUpdatePricingModel('fixed_per_stop')}
                  disabled={saving}
                >
                  Fixed
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Stops List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5 text-indigo-600" />
            Intermediate Stops ({stops.length})
          </h3>
          <Button onClick={() => setShowAddForm(true)} size="sm" disabled={showAddForm}>
            <Plus className="h-4 w-4 mr-1" /> Add Stop
          </Button>
        </div>

        {/* Add Stop Form */}
        {showAddForm && (
          <Card className="p-4 mb-4 border-2 border-dashed border-indigo-300 bg-indigo-50">
            <h4 className="font-medium mb-3">Add New Stop</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Input
                placeholder="City *"
                value={newStop.city}
                onChange={(e) => setNewStop({ ...newStop, city: e.target.value })}
              />
              <Input
                placeholder="Station *"
                value={newStop.station}
                onChange={(e) => setNewStop({ ...newStop, station: e.target.value })}
              />
              <Input
                placeholder="Arrival (mins from start) *"
                type="number"
                value={newStop.arrival_offset}
                onChange={(e) => setNewStop({ ...newStop, arrival_offset: e.target.value })}
              />
              <Input
                placeholder="Stop duration (mins)"
                type="number"
                value={newStop.stop_duration}
                onChange={(e) => setNewStop({ ...newStop, stop_duration: e.target.value })}
              />
              <Input
                placeholder="Distance from origin (km)"
                type="number"
                value={newStop.distance_from_origin}
                onChange={(e) => setNewStop({ ...newStop, distance_from_origin: e.target.value })}
              />
              {routeData?.pricingModel === 'fixed_per_stop' && (
                <Input
                  placeholder="Fixed Price (K)"
                  type="number"
                  value={newStop.fixed_price}
                  onChange={(e) => setNewStop({ ...newStop, fixed_price: e.target.value })}
                />
              )}
            </div>
            <div className="flex gap-2 mt-3">
              <Button onClick={handleAddStop} disabled={saving} size="sm">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Save Stop
              </Button>
              <Button variant="outline" onClick={() => setShowAddForm(false)} size="sm">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
            </div>
          </Card>
        )}

        {/* Stops List */}
        {stops.length > 0 ? (
          <div className="space-y-3">
            {stops.map((stop, index) => (
              <div
                key={index}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {editingIndex === index ? (
                  <EditStopForm
                    stop={stop}
                    pricingModel={routeData?.pricingModel}
                    onSave={(updated) => handleUpdateStop(index, updated)}
                    onCancel={() => setEditingIndex(null)}
                    saving={saving}
                  />
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center font-bold text-indigo-600">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-semibold">{stop.city}</p>
                        <p className="text-sm text-gray-500">{stop.station}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDuration(stop.arrivalOffset)} from start
                          </span>
                          {stop.distanceFromOrigin > 0 && (
                            <span>{stop.distanceFromOrigin} km</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-bold text-indigo-600">K{stop.estimatedPrice}</p>
                        <p className="text-xs text-gray-500">
                          {stop.fixedPrice ? 'Fixed' : `${Math.round(stop.priceMultiplier * 100)}%`}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingIndex(index)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteStop(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">
            <MapPin className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No intermediate stops configured</p>
            <p className="text-sm">Add stops to allow passengers to book to destinations along this route</p>
          </div>
        )}
      </Card>
    </div>
  );
};

// Edit Stop Form Component
const EditStopForm = ({ stop, pricingModel, onSave, onCancel, saving }) => {
  const [editedStop, setEditedStop] = useState({ ...stop });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Input
          placeholder="City"
          value={editedStop.city}
          onChange={(e) => setEditedStop({ ...editedStop, city: e.target.value })}
        />
        <Input
          placeholder="Station"
          value={editedStop.station}
          onChange={(e) => setEditedStop({ ...editedStop, station: e.target.value })}
        />
        <Input
          placeholder="Arrival (mins)"
          type="number"
          value={editedStop.arrivalOffset}
          onChange={(e) => setEditedStop({ ...editedStop, arrivalOffset: parseInt(e.target.value) })}
        />
        <Input
          placeholder="Stop duration"
          type="number"
          value={editedStop.stopDuration}
          onChange={(e) => setEditedStop({ ...editedStop, stopDuration: parseInt(e.target.value) })}
        />
        <Input
          placeholder="Distance (km)"
          type="number"
          value={editedStop.distanceFromOrigin || ''}
          onChange={(e) => setEditedStop({ ...editedStop, distanceFromOrigin: parseFloat(e.target.value) || null })}
        />
        {pricingModel === 'fixed_per_stop' && (
          <Input
            placeholder="Fixed Price (K)"
            type="number"
            value={editedStop.fixedPrice || ''}
            onChange={(e) => setEditedStop({ ...editedStop, fixedPrice: parseFloat(e.target.value) || null })}
          />
        )}
      </div>
      <div className="flex gap-2">
        <Button onClick={() => onSave(editedStop)} disabled={saving} size="sm">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
          Save
        </Button>
        <Button variant="outline" onClick={onCancel} size="sm">
          <X className="h-4 w-4 mr-1" /> Cancel
        </Button>
      </div>
    </div>
  );
};

export default RouteStopsManager;
